import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, Loader2, ScrollText, Info } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const MIN_LEN = 20;

const ROLE_LABELS = {
  company_admin: 'Administrador',
  compliance_officer: 'Compliance Officer',
  triage_manager: 'Gestor de Triagem',
  case_manager: 'Gestor de Denúncias',
  dpo: 'DPO',
};

const WorkflowApprovalDialog = ({ open, onOpenChange, report, stage, allowedRoles = [], onDecided }) => {
  const [action, setAction] = useState('APROVADO');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAction('APROVADO');
      setComments('');
    }
  }, [open]);

  const remaining = useMemo(() => Math.max(0, MIN_LEN - comments.trim().length), [comments]);
  const canSubmit = remaining === 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await adminApi.approveWorkflow(report.id, action, comments.trim());
      toast.success(action === 'APROVADO' ? 'Etapa aprovada' : 'Etapa rejeitada — denúncia arquivada');
      onDecided?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao decidir etapa');
    } finally {
      setSaving(false);
    }
  };

  const roleNames = (allowedRoles || []).map((r) => ROLE_LABELS[r] || r).join(', ') || '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="workflow-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-indigo-600" />
            Decidir etapa: {stage?.name || stage?.stage}
          </DialogTitle>
          <DialogDescription>
            Aprova ou rejeita esta etapa do workflow. A fundamentação fica no audit log e é visível na Case Timeline.
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-indigo-50 border-indigo-200">
          <Info className="w-4 h-4 text-indigo-600" />
          <AlertDescription className="text-sm text-indigo-900">
            <strong>Roles autorizados:</strong> {roleNames}. Administrador e Super Admin têm sempre permissão.
          </AlertDescription>
        </Alert>

        <div>
          <Label className="text-sm font-medium">Decisão</Label>
          <RadioGroup value={action} onValueChange={setAction} className="mt-2 grid grid-cols-2 gap-3">
            <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition ${action === 'APROVADO' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200'}`}>
              <RadioGroupItem value="APROVADO" data-testid="approval-action-approve" />
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1 text-emerald-800">
                  <CheckCircle className="w-4 h-4" />
                  Aprovar
                </div>
                <div className="text-xs text-slate-600 mt-1">Avança para a próxima etapa</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition ${action === 'REJEITADO' ? 'border-red-400 bg-red-50' : 'border-slate-200'}`}>
              <RadioGroupItem value="REJEITADO" data-testid="approval-action-reject" />
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1 text-red-700">
                  <XCircle className="w-4 h-4" />
                  Rejeitar
                </div>
                <div className="text-xs text-slate-600 mt-1">Denúncia é arquivada</div>
              </div>
            </label>
          </RadioGroup>
        </div>

        <div>
          <Label className="text-sm font-medium">Fundamentação (obrigatória)</Label>
          <Textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={
              action === 'APROVADO'
                ? 'Ex.: Análise inicial confirma admissibilidade — factos concretos, temporalidade compatível.'
                : 'Ex.: Denúncia manifestamente infundada — factos alegados não são verosímeis nem apresentam qualquer indício.'
            }
            className="mt-1 min-h-[120px]"
            data-testid="approval-comments-textarea"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-500">
              {remaining > 0
                ? `Faltam ${remaining} caracteres (mínimo ${MIN_LEN}).`
                : `${comments.trim().length} caracteres — pronto.`}
            </span>
            {remaining === 0 && <span className="text-xs text-emerald-600 font-medium">✓ válido</span>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={action === 'APROVADO' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
            data-testid="confirm-approval-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {action === 'APROVADO' ? 'Aprovar etapa' : 'Rejeitar etapa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowApprovalDialog;
